import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { IconClose } from '@/components/icons/Icons'
import { useLanguage } from '@/hooks/useLanguage'
import { t } from '@/utils/i18n'
import './Modal.css'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string | ReactNode
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  showCloseButton?: boolean
  /** true면 배경에 어둡게+블러 오버레이 적용 (예: 아이템 수정 모달) */
  showBackdrop?: boolean
  className?: string
  /** 오버레이에 적용할 추가 클래스 (z-index 등). 지정 시 아이템/다른 모달 위에 표시할 때 사용 */
  overlayClassName?: string
}

function Modal({ isOpen, onClose, title, children, size = 'md', showCloseButton = true, showBackdrop = false, className = '', overlayClassName = '' }: ModalProps) {
  const { language } = useLanguage()
  const mouseDownRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleMouseDown = (e: React.MouseEvent) => {
    // 모달 내부가 아닌 오버레이에서만 마우스 다운 위치 저장
    if (e.target === e.currentTarget) {
      mouseDownRef.current = { x: e.clientX, y: e.clientY }
    }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    // 모달 내부가 아닌 오버레이에서만 처리
    if (e.target === e.currentTarget && mouseDownRef.current) {
      const deltaX = Math.abs(e.clientX - mouseDownRef.current.x)
      const deltaY = Math.abs(e.clientY - mouseDownRef.current.y)
      
      // 드래그가 아닌 실제 클릭인 경우에만 모달 닫기 (5px 이내 이동은 클릭으로 간주)
      if (deltaX < 5 && deltaY < 5) {
        onClose()
      }
      mouseDownRef.current = null
    }
  }

  if (!isOpen) return null

  const modalNode = (
    <div
      className={`modal-overlay ${showBackdrop ? 'modal-overlay--with-backdrop' : ''} ${className ? `${className}-overlay` : ''} ${overlayClassName}`.trim()}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div
        className={`modal modal--${size} bg-white ${className || ''}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="modal__header bg-white">
            {typeof title === 'string' ? <h3>{title}</h3> : title}
            {showCloseButton && (
              <button type="button" className="modal__close" onClick={onClose} aria-label={t('common.close', language) || 'Close'}>
                <IconClose size={20} />
              </button>
            )}
          </div>
        )}
        <div className="modal__content bg-white">{children}</div>
      </div>
    </div>
  )

  return createPortal(modalNode, document.body)
}

export default Modal

